// src/components/salesRepDashboard/SalesRepOrderDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  IoArrowBack,
  IoPersonOutline,
  IoCalendarOutline,
  IoLocationOutline,
  IoMedkitOutline,
  IoDocumentTextOutline,
  IoCheckmarkCircle,
  IoAlertCircle,
  IoCallOutline,
  IoBusinessOutline,
  IoPencilOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoPrintOutline,
  IoResizeOutline,
  IoWaterOutline,
} from "react-icons/io5";
import { Loader2, Package, Truck } from "lucide-react";

/* ─────────────────────────────────────────────
   Accordion section wrapper
───────────────────────────────────────────── */
const AccordionSection = ({ icon, title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700/60 dark:bg-slate-800/60 dark:shadow-none">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-6 py-4 text-left transition hover:bg-slate-50 dark:hover:bg-slate-700/30"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl text-teal-600 dark:text-teal-400">{icon}</span>
          <span className="text-base font-bold tracking-tight text-slate-800 dark:text-slate-100">
            {title}
          </span>
        </div>
        {open ? (
          <IoChevronUpOutline className="text-lg text-slate-400 dark:text-slate-500" />
        ) : (
          <IoChevronDownOutline className="text-lg text-slate-400 dark:text-slate-500" />
        )}
      </button>

      {open && (
        <div className="border-t border-slate-100 px-6 py-5 dark:border-slate-700/60">
          {children}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Small label + bold value cell
───────────────────────────────────────────── */
const FieldCell = ({ label, value, accent = false }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
      {label}
    </p>
    <p
      className={`mt-1 text-sm font-bold ${
        accent
          ? "text-teal-600 dark:text-teal-400"
          : "text-slate-800 dark:text-slate-100"
      }`}
    >
      {value || "N/A"}
    </p>
  </div>
);

/* ─────────────────────────────────────────────
   Wound metric tile
───────────────────────────────────────────── */
const MetricTile = ({ icon, label, value }) => (
  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-700/50 dark:bg-slate-900/50">
    <div className="flex items-center gap-2">
      <span className="text-base text-teal-600 dark:text-teal-400">{icon}</span>
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {label}
      </span>
    </div>
    <p className="mt-2 text-lg font-extrabold text-slate-800 dark:text-slate-100">
      {value ?? "N/A"}
    </p>
  </div>
);

/* ─────────────────────────────────────────────
   Delivery row
───────────────────────────────────────────── */
const DeliveryRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 py-3">
    <span className="mt-0.5 text-lg text-teal-600 dark:text-teal-400">{icon}</span>
    <div>
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-bold text-slate-700 dark:text-slate-200">
        {value || "N/A"}
      </p>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   Date pill
───────────────────────────────────────────── */
const DatePill = ({ icon, label, value }) => (
  <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold shadow-sm dark:border-slate-700/60 dark:bg-slate-800/60">
    <span className="text-teal-600 dark:text-teal-400">{icon}</span>
    <span className="text-slate-400 dark:text-slate-500">{label}:</span>
    <span className="font-bold text-slate-700 dark:text-slate-200">{value}</span>
  </div>
);

/* ─────────────────────────────────────────────
   Care package row
───────────────────────────────────────────── */
const CareRow = ({ label, sub, badge, detail }) => (
  <div className="flex items-center justify-between rounded-xl px-1 py-3 transition hover:bg-slate-50 dark:hover:bg-slate-700/20">
    <div>
      <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{label}</p>
      {sub && <p className="text-xs text-slate-400 dark:text-slate-500">{sub}</p>}
      {detail && (
        <p className="mt-0.5 text-xs text-teal-600 dark:text-teal-400">{detail}</p>
      )}
    </div>
    {badge && (
      <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
        {badge}
      </span>
    )}
  </div>
);

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
const SalesRepOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const [notesText, setNotesText] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [notesError, setNotesError] = useState(null);

  useEffect(() => {
    fetchOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const response = await fetch(
        `https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/carekit/${orderId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch order details");
      const data = await response.json();
      setOrderDetails(data);
      setNotesText(data.salesrep_notes || "");
    } catch (err) {
      setErrorMessage(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setIsSavingNotes(true);
      setNotesError(null);
      setNotesSaved(false);
      const response = await fetch(
        `https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/orders/${orderId}/notes/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ salesrep_notes: notesText }),
        }
      );
      if (!response.ok) throw new Error("Failed to save notes");
      setNotesSaved(true);
      setOrderDetails((prev) => ({ ...prev, salesrep_notes: notesText }));
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (err) {
      setNotesError(err?.message || "Failed to save notes");
    } finally {
      setIsSavingNotes(false);
    }
  };

  const formatDate = (value, mode = "date") => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "N/A";
    if (mode === "datetime")
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const statusConfig = useMemo(() => {
    const s = String(
      orderDetails?.status || orderDetails?.status_display || ""
    ).toLowerCase();
    if (s.includes("delivered"))
      return {
        label: orderDetails?.status_display || "Delivered",
        cls: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30",
        dot: "bg-emerald-500 dark:bg-emerald-400",
      };
    if (s.includes("cancel"))
      return {
        label: orderDetails?.status_display || "Cancelled",
        cls: "bg-rose-50 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-500/30",
        dot: "bg-rose-500 dark:bg-rose-400",
      };
    if (s.includes("ship"))
      return {
        label: orderDetails?.status_display || "Shipped",
        cls: "bg-teal-50 text-teal-700 ring-1 ring-teal-200 dark:bg-teal-500/15 dark:text-teal-300 dark:ring-teal-500/30",
        dot: "bg-teal-500 dark:bg-teal-400",
      };
    if (s.includes("process"))
      return {
        label: orderDetails?.status_display || "Processing",
        cls: "bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-500/15 dark:text-sky-300 dark:ring-sky-500/30",
        dot: "bg-sky-500 dark:bg-sky-400",
      };
    return {
      label: orderDetails?.status_display || "Pending",
      cls: "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30",
      dot: "bg-amber-500 dark:bg-amber-400",
    };
  }, [orderDetails?.status, orderDetails?.status_display]);

  const notesChanged = notesText !== (orderDetails?.salesrep_notes || "");

  const address = [
    orderDetails?.street,
    orderDetails?.city,
    orderDetails?.zip_code,
    orderDetails?.country,
  ]
    .filter(Boolean)
    .join(", ");

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900/70">
          <Loader2 className="h-9 w-9 animate-spin text-teal-600 dark:text-teal-400" />
          <div>
            <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
              Loading order details
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Fetching the latest information…
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (errorMessage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-slate-950">
        <div className="w-full max-w-sm rounded-2xl border border-rose-200 bg-white p-6 shadow-sm dark:border-rose-500/30 dark:bg-slate-900/80">
          <div className="flex items-start gap-3">
            <IoAlertCircle className="mt-0.5 text-2xl text-rose-500 dark:text-rose-400" />
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Unable to load order
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {errorMessage}
              </p>
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Go Back
            </button>
            <button
              onClick={fetchOrderDetails}
              className="flex-1 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 py-2.5 text-sm font-bold text-white transition hover:from-teal-700 hover:to-emerald-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!orderDetails) return null;

  return (
    <div className="min-h-screen bg-slate-50 pb-16 dark:bg-slate-950">

      {/* ── Sticky top header ── */}
      <div className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          {/* Left */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
            >
              <IoArrowBack className="text-base transition group-hover:-translate-x-0.5" />
              Back
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />

            <div className="flex items-center gap-3">
              <h1 className="text-base font-extrabold tracking-tight text-slate-900 dark:text-slate-100 sm:text-lg">
                Order #{orderDetails.order_number}
              </h1>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${statusConfig.cls}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${statusConfig.dot}`} />
                {statusConfig.label}
              </span>
            </div>
          </div>

          {/* Right: print */}
          {/* <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-teal-500"
          >
            <IoPrintOutline className="text-base" />
            Print Labels
          </button> */}
        </div>
      </div>

      {/* ── Date pills ── */}
      <div className="mx-auto max-w-3xl px-4 py-5 sm:px-6">
        <div className="flex flex-wrap gap-2">
          <DatePill
            icon={<IoCalendarOutline />}
            label="Created"
            value={formatDate(orderDetails.created_at)}
          />
          <DatePill
            icon={<Truck size={13} />}
            label="Shipped"
            value={orderDetails.shipped_at ? formatDate(orderDetails.shipped_at) : "—"}
          />
          <DatePill
            icon={<IoCalendarOutline />}
            label="Expected"
            value={orderDetails.delivery_date ? formatDate(orderDetails.delivery_date) : "—"}
          />
        </div>
      </div>

      {/* ── Accordion sections ── */}
      <div className="mx-auto max-w-3xl space-y-3 px-4 sm:px-6">

        {/* Patient Information */}
        <AccordionSection
          icon={<IoPersonOutline />}
          title="Patient Information"
          defaultOpen
        >
          <div className="grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-4">
            <FieldCell label="Name" value={orderDetails.patient_name} />
            <FieldCell
              label="Patient ID"
              value={orderDetails.patient ? `#PT-${orderDetails.patient}` : null}
            />
            <FieldCell label="ICD-10 Code" value={orderDetails.icd10_code} accent />
            <FieldCell
              label="Chronic Wound"
              value={orderDetails.is_chronic_wound ? "Yes" : "No"}
            />
          </div>
        </AccordionSection>

        {/* Wound Details */}
        <AccordionSection
          icon={<IoMedkitOutline />}
          title="Wound Details"
          defaultOpen
        >
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MetricTile
              icon={<IoMedkitOutline />}
              label="Wound Type"
              value={orderDetails.wound_type}
            />
            <MetricTile
              icon={<IoLocationOutline />}
              label="Location"
              value={orderDetails.wound_location}
            />
            <MetricTile
              icon={<IoWaterOutline />}
              label="Drainage"
              value={
                orderDetails.wound_drainage_display
                  ? String(orderDetails.wound_drainage_display)
                      .toLowerCase()
                      .replace(/^\w/, (c) => c.toUpperCase())
                  : null
              }
            />
            <MetricTile
              icon={<IoResizeOutline />}
              label="Depth"
              value={orderDetails.wound_depth != null ? `${orderDetails.wound_depth} cm` : null}
            />
            <MetricTile
              icon={<IoResizeOutline />}
              label="Length"
              value={orderDetails.wound_length != null ? `${orderDetails.wound_length} cm` : null}
            />
            <MetricTile
              icon={<IoResizeOutline />}
              label="Width"
              value={orderDetails.wound_width != null ? `${orderDetails.wound_width} cm` : null}
            />
            <MetricTile
              icon={<IoCheckmarkCircle />}
              label="Full Thickness"
              value={orderDetails.is_full_thickness ? "Yes" : "No"}
            />
            <MetricTile
              icon={<IoCheckmarkCircle />}
              label="Conservative"
              value={orderDetails.conservative_care ? "Yes" : "No"}
            />
          </div>
        </AccordionSection>

        {/* Care Package Details */}
        <AccordionSection
          icon={<Package size={18} />}
          title="Care Package Details"
          defaultOpen
        >
          <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2 dark:border-slate-700/60">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              Item Description
            </span>
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              Detail
            </span>
          </div>

          <div className="divide-y divide-slate-100 dark:divide-slate-700/30">
            <CareRow
              label="Care Type"
              sub={orderDetails.care_type_display}
              badge={orderDetails.kit_duration_display}
            />
            {orderDetails.care_type === "propacks" && orderDetails.propack_display && (
              <CareRow
                label="ProPack Configuration"
                sub="Primary + Secondary bundle"
                detail={orderDetails.propack_display}
              />
            )}
            {orderDetails.care_type === "advanced" && orderDetails.graft_display && (
              <CareRow
                label="Graft Details"
                sub="Advanced care graft"
                detail={orderDetails.graft_display}
              />
            )}
          </div>
        </AccordionSection>

        {/* Delivery & Facility */}
        <AccordionSection
          icon={<IoLocationOutline />}
          title="Delivery & Facility"
        >
          <div className="divide-y divide-slate-100 dark:divide-slate-700/40">
            <DeliveryRow
              icon={<IoBusinessOutline />}
              label="Facility"
              value={orderDetails.facility_name}
            />
            <DeliveryRow
              icon={<IoLocationOutline />}
              label="Address"
              value={address || "N/A"}
            />
            <DeliveryRow
              icon={<IoCallOutline />}
              label="Phone"
              value={orderDetails.phone_number}
            />
            {orderDetails.tracking_number && (
              <DeliveryRow
                icon={<Truck size={16} />}
                label="Tracking Number"
                value={orderDetails.tracking_number}
              />
            )}
          </div>
        </AccordionSection>

        {/* Order Summary */}
        <AccordionSection
          icon={<IoDocumentTextOutline />}
          title="Order Summary"
        >
          <div className="grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-3">
            <FieldCell label="Order Number" value={orderDetails.order_number} accent />
            <FieldCell label="Status" value={statusConfig.label} />
            <FieldCell
              label="Created"
              value={formatDate(orderDetails.created_at, "datetime")}
            />
            <FieldCell
              label="Shipped"
              value={
                orderDetails.shipped_at
                  ? formatDate(orderDetails.shipped_at, "datetime")
                  : "—"
              }
            />
            <FieldCell
              label="Expected Delivery"
              value={
                orderDetails.delivery_date
                  ? formatDate(orderDetails.delivery_date)
                  : "—"
              }
            />
          </div>
        </AccordionSection>

        {/* Sales Rep Notes */}
        <AccordionSection
          icon={<IoPencilOutline />}
          title="Sales Rep Notes"
        >
          <p className="mb-3 text-xs text-slate-400 dark:text-slate-500">
            Private notes visible only to you.
          </p>

          <textarea
            value={notesText}
            onChange={(e) => {
              setNotesText(e.target.value);
              setNotesSaved(false);
              setNotesError(null);
            }}
            rows={4}
            placeholder="Add your notes here…"
            className={
              "w-full resize-none rounded-xl border px-3 py-2.5 text-sm " +
              "bg-slate-50 text-slate-800 placeholder:text-slate-400 " +
              "border-slate-200 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-400/20 " +
              "dark:bg-slate-900/60 dark:text-slate-100 dark:placeholder:text-slate-600 " +
              "dark:border-slate-700 dark:focus:border-teal-500/50 dark:focus:ring-teal-500/20"
            }
          />

          {notesError && (
            <p className="mt-2 text-xs font-semibold text-rose-500 dark:text-rose-400">
              {notesError}
            </p>
          )}
          {notesSaved && (
            <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              <IoCheckmarkCircle /> Notes saved
            </p>
          )}

          <button
            onClick={handleSaveNotes}
            disabled={isSavingNotes || !notesChanged}
            className={
              "mt-3 inline-flex w-full items-center justify-center rounded-xl py-2.5 text-sm font-bold transition " +
              (isSavingNotes || !notesChanged
                ? "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600"
                : "bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-500 hover:to-emerald-500")
            }
          >
            {isSavingNotes ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save Notes"
            )}
          </button>

          {orderDetails.salesrep_notes && (
            <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-3 dark:border-slate-700/50 dark:bg-slate-900/40">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                Last Saved
              </p>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                {orderDetails.salesrep_notes}
              </p>
            </div>
          )}
        </AccordionSection>

      </div>
    </div>
  );
};

export default SalesRepOrderDetail;